import { useTranslation } from "react-i18next";
import { useState } from "react";
import dynamic from "next/dynamic";
import { BarChart3, Network } from "lucide-react";
import ProductionTable from "./ProductionTable";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import type {
  ItemId,
  RecipeId,
  Item,
  Facility,
  ProductionDependencyGraph,
  VisualizationMode,
} from "@/types";
import type { ProductionLineData } from "./ProductionTable";

const ProductionDependencyTree = dynamic(
  () => import("../flow/ProductionDependencyTree"),
  {
    ssr: false,
    loading: () => (
      <div className="h-full w-full flex items-center justify-center text-muted-foreground">
        Loading tree view...
      </div>
    ),
  },
);

interface ProductionViewTabsProps {
  plan: ProductionDependencyGraph | null;
  tableData: ProductionLineData[];
  items: Item[];
  facilities: Facility[];
  activeTab: "table" | "tree";
  onTabChange: (tab: "table" | "tree") => void;
  onRecipeChange: (itemId: ItemId, recipeId: RecipeId) => void;
  onToggleRawMaterial: (itemId: ItemId) => void;
}

export default function ProductionViewTabs({
  plan,
  tableData,
  items,
  facilities,
  activeTab,
  onTabChange,
  onRecipeChange,
  onToggleRawMaterial,
}: ProductionViewTabsProps) {
  const { t } = useTranslation("app");
  const [visualizationMode, setVisualizationMode] =
    useState<VisualizationMode>("merged");

  return (
    <div className="flex-1 min-w-0">
      <Card className="h-full flex flex-col">
        <Tabs
          value={activeTab}
          onValueChange={(val) => onTabChange(val as "table" | "tree")}
          className="h-full flex flex-col"
        >
          <CardHeader className="shrink-0">
            <div className="flex items-center justify-between gap-4">
              <TabsList className="grid w-full max-w-md grid-cols-2">
                <TabsTrigger value="table" className="gap-2">
                  <BarChart3 className="h-4 w-4" />
                  <span>{t("tabs.table")}</span>
                </TabsTrigger>
                <TabsTrigger value="tree" className="gap-2">
                  <Network className="h-4 w-4" />
                  <span>{t("tabs.tree")}</span>
                </TabsTrigger>
              </TabsList>

              {activeTab === "tree" && (
                <ToggleGroup
                  type="single"
                  value={visualizationMode}
                  onValueChange={(value) => {
                    if (value) setVisualizationMode(value as VisualizationMode);
                  }}
                >
                  <ToggleGroupItem value="merged" aria-label="Merged view">
                    <span className="text-xs">{t("tabs.merged")}</span>
                  </ToggleGroupItem>
                  <ToggleGroupItem value="separated" aria-label="Separated view">
                    <span className="text-xs">{t("tabs.separated")}</span>
                  </ToggleGroupItem>
                </ToggleGroup>
              )}
            </div>
          </CardHeader>
          <CardContent className="flex-1 min-h-0 overflow-hidden p-0">
            <TabsContent value="table" className="h-full m-0 p-4 pt-0">
              <div className="h-full overflow-auto">
                <ProductionTable
                  data={tableData}
                  items={items}
                  facilities={facilities}
                  onRecipeChange={onRecipeChange}
                  onToggleRawMaterial={onToggleRawMaterial}
                />
              </div>
            </TabsContent>
            <TabsContent value="tree" className="h-full m-0">
              {activeTab === "tree" && (
                <ProductionDependencyTree
                  plan={plan}
                  items={items}
                  facilities={facilities}
                  visualizationMode={visualizationMode}
                />
              )}
            </TabsContent>
          </CardContent>
        </Tabs>
      </Card>
    </div>
  );
}
